import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { stats, issues } from '../utils/mockData';
import ReactECharts from 'echarts-for-react';

const { FiTrendingUp, FiZap, FiAlertCircle, FiDollarSign, FiActivity } = FiIcons;

const StatCard = ({ title, value, icon, color = 'blue', delay = 0 }) => {
  const isOrange = color === 'orange';
  const textColor = isOrange ? 'text-[#ff5500]' : 'text-[#00f0ff]';
  const glowClass = isOrange ? 'text-glow-orange' : 'text-glow-blue';

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`hud-panel p-6 flex flex-col justify-between min-h-[140px] relative overflow-hidden group ${isOrange ? 'border-[#ff5500]/30' : 'border-white/5'}`}
    >
      <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform duration-500">
        <SafeIcon icon={icon} className="text-6xl" />
      </div>
      <div>
        <p className="text-[10px] font-mono text-gray-500 uppercase tracking-[0.3em] mb-2">{title}</p>
        <h3 className={`text-3xl font-bold ${textColor} ${glowClass} tracking-tighter`}>{value}</h3>
      </div>
      <div className="flex items-center gap-2 text-[9px] font-mono text-gray-600 uppercase tracking-widest pt-4 border-t border-white/5">
        <SafeIcon icon={FiActivity} className={isOrange ? 'animate-pulse' : ''} />
        Real-time Pulse Verified
      </div>
    </motion.div>
  );
};

const Dashboard = () => {
  const chartOptions = {
    backgroundColor: 'transparent',
    textStyle: { fontFamily: 'monospace' },
    tooltip: { trigger: 'axis', backgroundColor: '#0a0a12', borderColor: '#1a1a2e', textStyle: { color: '#ccc' } },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { 
      type: 'category', 
      boundaryGap: false, 
      data: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'],
      axisLine: { lineStyle: { color: '#1a1a2e' } },
      axisLabel: { color: '#666', fontSize: 10 }
    },
    yAxis: { 
      type: 'value',
      splitLine: { lineStyle: { color: '#1a1a2e', type: 'dashed' } },
      axisLabel: { color: '#666', fontSize: 10 }
    },
    series: [
      {
        name: 'Fleet Activity',
        type: 'line',
        smooth: true,
        lineStyle: { color: '#00f0ff', width: 3, shadowColor: '#00f0ff', shadowBlur: 15 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color: 'rgba(0,240,255,0.2)' }, { offset: 1, color: 'rgba(0,240,255,0)' }]
          }
        },
        data: [12, 15, 65, 84, 78, 45, 20]
      }
    ]
  };

  return (
    <div className="space-y-8 pb-12">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tighter uppercase">Command <span className="text-[#00f0ff] text-glow-blue">Core</span></h1>
          <p className="text-xs font-mono text-gray-500 uppercase tracking-widest mt-1">Tactical Fleet Situational Awareness Dashboard</p>
        </div>
        <div className="font-mono text-[10px] text-[#00f0ff] flex items-center gap-3 bg-[#00f0ff]/5 px-4 py-2 rounded-full border border-[#00f0ff]/20">
          <span className="w-2 h-2 rounded-full bg-[#00f0ff] animate-pulse shadow-[0_0_8px_#00f0ff]" />
          SYNC_ACTIVE: {new Date().toLocaleTimeString()}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Active Fleet" value={stats.activeFleet} icon={FiZap} delay={0.1} />
        <StatCard title="Avg Efficiency" value={stats.fuelEfficiency} icon={FiTrendingUp} delay={0.2} />
        <StatCard title="Critical Issues" value={stats.totalIssues} icon={FiAlertCircle} color="orange" delay={0.3} />
        <StatCard title="Est. Daily Cost" value={stats.dailyCost} icon={FiDollarSign} delay={0.4} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <motion.div 
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
          className="lg:col-span-2 hud-panel p-8 flex flex-col bg-gradient-to-br from-[#0a0a12] to-[#050508]"
        >
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-xs font-mono text-gray-500 uppercase tracking-[0.3em]">Activity Telemetry (24H)</h3>
            <div className="flex items-center gap-4 text-[10px] font-mono uppercase">
              <div className="flex items-center gap-2"><div className="w-2 h-2 bg-[#00f0ff] rounded-full"></div> Units_Active</div>
            </div>
          </div>
          <div className="flex-1 min-h-[350px]">
             <ReactECharts option={chartOptions} style={{ height: '100%', width: '100%' }} />
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="hud-panel p-8 flex flex-col bg-[#0a0a12]/50 border-white/5"
        >
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-xs font-mono text-gray-500 uppercase tracking-[0.3em]">Priority Log</h3>
            <span className="text-[9px] font-mono bg-[#ff5500]/10 text-[#ff5500] px-3 py-1 rounded-full border border-[#ff5500]/30 animate-pulse uppercase tracking-widest font-bold">Live</span>
          </div>
          <div className="space-y-4 overflow-y-auto pr-2 scrollbar-hide flex-1">
            {issues.map(issue => (
              <div key={issue.id} className="bg-[#050508] border border-[#1a1a2e] p-5 rounded-xl hover:border-[#ff5500]/40 transition-all group cursor-pointer relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1 h-full bg-[#1a1a2e] group-hover:bg-[#ff5500]/40"></div>
                <div className="flex justify-between items-start mb-3">
                  <span className="text-[10px] font-mono text-[#ff5500] font-bold tracking-widest">{issue.id}</span>
                  <span className="text-[9px] font-mono text-gray-600 font-medium uppercase">{issue.time}</span>
                </div>
                <p className="text-sm text-gray-200 font-bold mb-2 group-hover:text-[#ff5500] transition-colors leading-tight uppercase tracking-wide">{issue.title}</p>
                <div className="flex items-center gap-2 text-[10px] font-mono text-gray-500 uppercase">
                  <SafeIcon icon={FiIcons.FiTruck} className="text-[#00f0ff]" /> {issue.vehicle}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;